import { useInfiniteQuery } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import FacebookPagesFilter from '@/components/ChannelsFilter/FacebookPagesFilter';
import { Controls, ControlsButton } from '@/components/Controls';
import DebouncedSearchBar from '@/components/DebouncedSearchBar';
import ExportButton from '@/components/ExportButton';
import FiltersDropdown from '@/components/FilterDropdown';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import FilterRadioButton from '@/components/FilterDropdown/FilterRadioButton';
import {
  FilterGroupBody,
  FilterGroupHeader,
  FilterGroupTitle,
  FilterWrap,
} from '@/components/FilterDropdown/Parts';
import FullScreenButton from '@/components/FullScreenButton';
import { PageHeading, SectionHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import {
  ShowMoreButton,
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
  Thumbnail,
} from '@/components/Table';
import { ExtraLargeTh } from '@/components/Table';
import Text from '@/components/Text';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import { selectTeamSettings } from '@/selectors';
import {
  DistributionVideoResource,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import {
  DistributionDepartmentsEnum,
  ReleaseStatusEnum,
  SortDirection,
} from '@/utils/enums';
import { RootState } from '@/utils/store';

import DistributionVideoFilter from './DistributionVideoFilter';
import DistributionVideoTableRow from './DistributionVideoTableRow';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DESC,
  property: 'views',
};

const columns = [
  {
    title: 'Project ID',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Thumbnail',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Video Title',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Fresh ID',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Network & Channel',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Network & Channel',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Type',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Scheduled Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Release Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Total Views',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Director',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Editor',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Publisher',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Archivist',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Link',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Part In',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Compilation Group',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Recipe Status',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Description',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Notes',
    toggable: true,
    defaultVisibility: true,
  },
];

export enum Tabs {
  Published = ReleaseStatusEnum.POSTED,
  Sheduled = ReleaseStatusEnum.SCHEDULED,
  All = -1,
}

export const StyledTable = styled(Table)`
  ${Thumbnail} {
    max-width: 92px;
  }
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      padding-left: 24px;
      width: 130px;
      min-width: 130px;
      position: sticky;
      z-index: 1;
      left: 0;
      top: 0;
      bottom: 0;
      background-color: ${({ theme }) => theme.colors.white};
    }
    &:nth-child(2) {
      width: 116px;
      min-width: 116px;
      max-width: 116px;
    }
    &:nth-child(3) {
      min-width: 144px;
    }
    &:last-child {
      padding-right: 24px;
      position: relative;
    }
  }
`;

const StyledSectionHeading = styled(SectionHeading)`
  margin-bottom: 0;
`;

const ButtonsHolder = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledPageHeading = styled(PageHeading)`
  span {
    font-weight: 400;
  }
`;

type LocationState =
  | {
      tab?: Tabs;
    }
  | undefined;

interface Props {
  isProjectSummary?: boolean;
}

const DistributionVideoTable: FC<Props> = ({ isProjectSummary = false }) => {
  const { teamId, projectId, status } = useParams();

  const navigate = useNavigate();

  const getTabFromStatus = () => {
    switch (status) {
      case 'published':
        return Tabs.Published;
      case 'scheduled':
        return Tabs.Sheduled;
      default:
        return Tabs.All;
    }
  };

  useEffect(() => {
    status !== 'published' &&
      status !== 'scheduled' &&
      status !== undefined &&
      navigate('/404', {
        replace: true,
      });
  }, [status, navigate]);

  const location = useLocation();

  const locationState = location.state as LocationState;

  const activeTab = locationState?.tab ?? getTabFromStatus();

  const tableViewOptions = useSelector(
    (rootState: RootState) =>
      rootState.options.tableViews['distributionVideoSummaryTable'],
  );

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [search, setSearch] = useState<string>('');

  const [showDepartmentFilter, setShowDepartmentFilter] = useState(false);
  const [activeDepartment, setActiveDepartment] = useState(
    DistributionDepartmentsEnum.ALL,
  );
  const [activeType, setActiveType] = useState<null | 0 | 1>(null);
  const [showTypeFilter, setShowTypeFilter] = useState(false);

  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!),
  );

  const params = {
    ...(activeType !== null && {
      'filter[isReel]': activeType,
    }),
    ...(activeDepartment !== DistributionDepartmentsEnum.ALL && {
      'filter[is3rdParty]':
        activeDepartment === DistributionDepartmentsEnum.NATIVE ? '0' : '1',
    }),
    ...((activeTab === Tabs.Published || activeTab === Tabs.Sheduled) && {
      'filter[status]': activeTab,
    }),
    ...(isProjectSummary && {
      'filter[status]': Tabs.Published,
    }),
    // ...(teamId !== "all" && {
    //   "filter[teamId]": teamId,
    // }),
    ...(!!projectId && {
      'filter[projectId]': projectId,
    }),
    ...(!!selectedChannels.length && {
      'filter[channelId]': selectedChannels.join(','),
    }),
    ...(sort.direction !== 'DEFAULT' && {
      sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
    }),
    ...(!!search && {
      'filter[search]': search,
    }),
    ...(!!teamSettings?.distribution_videos_last_release &&
      !isProjectSummary && {
        'filter[lastRelease]': teamSettings.distribution_videos_last_release,
      }),
  };

  const fetchCsv = () =>
    api.get<never, string>(`/v1/releases/video-summary/export`, {
      params,
    });

  const fetchReleases = ({ pageParam = '/v1/releases/video-summary' }) =>
    api.get<never, PaginatedResponse<DistributionVideoResource>>(pageParam, {
      params,
    });

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<DistributionVideoResource>>(
    [
      'releases',
      {
        videoSummary: true,
        // teamId,
        projectId,
        selectedChannels,
        sort,
        search,
        lastRelease: teamSettings?.distribution_videos_last_release,
        activeTab,
        activeDepartment,
        activeType,
      },
    ],
    fetchReleases,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 120 * 1000,
    },
  );

  const videos = useMemo(
    () =>
      data?.pages?.reduce<DistributionVideoResource[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  return (
    <>
      {!!projectId && (
        <StyledPageHeading>
          Project ID: <span>{projectId}</span>
        </StyledPageHeading>
      )}
      {!!!projectId && (
        <>
          <PageHeading>Video Summary</PageHeading>
          <TableHeader>
            <Controls>
              <ControlsButton
                active={activeTab === Tabs.Published}
                as={Link}
                to={`/teams/${teamId}/distribution/videos/published`}
              >
                Published
              </ControlsButton>
              <ControlsButton
                active={activeTab === Tabs.Sheduled}
                as={Link}
                to={`/teams/${teamId}/distribution/videos/scheduled`}
              >
                Sheduled
              </ControlsButton>
              <ControlsButton
                active={activeTab === Tabs.All}
                as={Link}
                to={`/teams/${teamId}/distribution/videos`}
              >
                All
              </ControlsButton>
            </Controls>
            <ButtonsHolder>
              <ExportButton
                fetchCsv={fetchCsv}
                fileName={`distribution-video-summary.csv`}
              />
              <ViewOptionsModal
                columns={columns}
                tableKey="distributionVideoSummaryTable"
              />
              <FilterWrap>
                <FilterButton
                  isActive={showDepartmentFilter}
                  text="Department"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDepartmentFilter((prev) => !prev);
                  }}
                  filterApplied={
                    activeDepartment !== DistributionDepartmentsEnum.ALL
                  }
                />
                {showDepartmentFilter && (
                  <FiltersDropdown
                    onClose={() => setShowDepartmentFilter(false)}
                  >
                    <FilterGroupHeader>
                      <FilterGroupTitle>Department</FilterGroupTitle>
                    </FilterGroupHeader>
                    <FilterGroupBody>
                      <FilterRadioButton
                        id="departmentAll"
                        value={DistributionDepartmentsEnum.ALL.toString()}
                        checked={
                          activeDepartment === DistributionDepartmentsEnum.ALL
                        }
                        setValue={() =>
                          setActiveDepartment(DistributionDepartmentsEnum.ALL)
                        }
                      >
                        All
                      </FilterRadioButton>
                      <FilterRadioButton
                        id="departmentNative"
                        value={DistributionDepartmentsEnum.NATIVE.toString()}
                        checked={
                          activeDepartment ===
                          DistributionDepartmentsEnum.NATIVE
                        }
                        setValue={() =>
                          setActiveDepartment(
                            DistributionDepartmentsEnum.NATIVE,
                          )
                        }
                      >
                        Native
                      </FilterRadioButton>
                      <FilterRadioButton
                        id="department3rdPt"
                        value={DistributionDepartmentsEnum.THIRD_PT.toString()}
                        checked={
                          activeDepartment ===
                          DistributionDepartmentsEnum.THIRD_PT
                        }
                        setValue={() =>
                          setActiveDepartment(
                            DistributionDepartmentsEnum.THIRD_PT,
                          )
                        }
                      >
                        3rd Party
                      </FilterRadioButton>
                    </FilterGroupBody>
                  </FiltersDropdown>
                )}
              </FilterWrap>
              <FilterWrap>
                <FilterButton
                  isActive={showTypeFilter}
                  text="Type"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTypeFilter((prev) => !prev);
                  }}
                  filterApplied={activeType !== null}
                />
                {showTypeFilter && (
                  <FiltersDropdown onClose={() => setShowTypeFilter(false)}>
                    <FilterGroupHeader>
                      <FilterGroupTitle>By Type:</FilterGroupTitle>
                    </FilterGroupHeader>
                    <FilterGroupBody>
                      <FilterRadioButton
                        id="typeAll"
                        value={''}
                        checked={activeType === null}
                        setValue={() => setActiveType(null)}
                      >
                        All
                      </FilterRadioButton>
                      <FilterRadioButton
                        id="typeVideo"
                        value={'0'}
                        checked={activeType === 0}
                        setValue={() => setActiveType(0)}
                      >
                        Video
                      </FilterRadioButton>
                      <FilterRadioButton
                        id="typeReels"
                        value={'1'}
                        checked={activeType === 1}
                        setValue={() => setActiveType(1)}
                      >
                        Reels
                      </FilterRadioButton>
                    </FilterGroupBody>
                  </FiltersDropdown>
                )}
              </FilterWrap>
              <FullScreenButton />
            </ButtonsHolder>
          </TableHeader>
          <FacebookPagesFilter setChannels={setSelectedChannels} />
          <DebouncedSearchBar
            placeholder="Search by Project ID or Fresh ID"
            setValue={setSearch}
          />
        </>
      )}
      <TableHeader>
        <StyledSectionHeading>Video list</StyledSectionHeading>
        {teamId !== 'all' && !!!projectId && <DistributionVideoFilter />}
        {!!projectId && (
          <ButtonsHolder>
            <ExportButton
              fetchCsv={fetchCsv}
              fileName={`distribution-video-summary.csv`}
            />
            <ViewOptionsModal
              columns={columns}
              tableKey="distributionVideoSummaryTable"
            />
            <FullScreenButton />
          </ButtonsHolder>
        )}
      </TableHeader>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Project ID</Th>
              <Th>Thumbnail</Th>
              <ExtraLargeTh>Video Title</ExtraLargeTh>
              {tableViewOptions['Fresh ID'] && (
                <SortableTh
                  property="freshId"
                  title="Fresh ID"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Network & Channel'] && (
                <SortableTh
                  property="channelName"
                  title="Channel Name"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Type'] && (
                <SortableTh
                  property="isReel"
                  title="Type"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Compilation Type'] && (
                <SortableTh
                  property="compilationType"
                  title="Compilation Type"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Scheduled Date'] && (
                <SortableTh
                  property="createdAt"
                  title="Scheduled Date"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Release Date'] && (
                <SortableTh
                  property="releasedAt"
                  title="Release Date"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Total Views'] && (
                <SortableTh
                  property="views"
                  title="Total Views"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Director'] && <Th>Director</Th>}
              {tableViewOptions['Editor'] && <Th>Editor</Th>}
              {tableViewOptions['Publisher'] && <Th>Publisher</Th>}
              {tableViewOptions['Archivist'] && <Th>Archivist</Th>}
              {tableViewOptions['Link'] && <Th>Link</Th>}
              {tableViewOptions['Part In'] && (
                <SortableTh
                  property="part"
                  title="Part In"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Compilation Group'] && (
                <SortableTh
                  property="compilationGroup"
                  title="Compilation Group"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Recipe Status'] && (
                <SortableTh
                  property="tipsCount"
                  title="Recipe Status"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Description'] && (
                <ExtraLargeTh>Description</ExtraLargeTh>
              )}
              {tableViewOptions['Notes'] && (
                <ExtraLargeTh>
                  Note{' '}
                  <Text as="span" $color="gray" $type="smallHeadline">
                    (Internal Information)
                  </Text>
                </ExtraLargeTh>
              )}
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!videos?.length}
            >
              {videos?.map((video) => (
                <DistributionVideoTableRow key={video.id} video={video} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      {hasNextPage && (
        <ShowMoreButton loading={isFetchingNextPage} onClick={fetchNextPage} />
      )}
    </>
  );
};

export default DistributionVideoTable;
